/**
 * §P.2 · La asistencia, de través.
 *
 * ## La pregunta que contesta
 *
 * «¿Quién faltó el martes?» y «¿cuántas veces ha faltado este niño?». Las dos se
 * podían contestar antes abriendo clase por clase, y por eso no se contestaban.
 *
 * No sustituye al informe de §P.9: aquel da el agregado por franja —«el martes a las
 * 17:30 viene el 78 %»— y sirve para decidir horarios. Esto da las filas, y sirve
 * para llamar a una familia.
 *
 * ## La ventana inicial depende de por dónde se entra, y eso no es un detalle
 *
 * Son dos preguntas distintas con la misma pantalla. Desde la pestaña: «¿quién faltó
 * esta semana?» —siete días—. Desde la ficha de un alumno: «¿cómo va este niño?», que
 * en siete días no se ve; ahí entra por el trimestre.
 *
 * La primera versión entraba siempre por la semana, y al pulsar «ver su asistencia»
 * en una ficha salía «no hay nada en esas fechas». Un enlace que promete algo y lleva
 * a un vacío es peor que no tener el enlace: quien lo pulsa concluye que el dato no
 * está, no que el filtro es corto. Se vio mirando, el 18-09-2026.
 *
 * Lo que no cambia es que **siempre hay ventana**: un club con doscientos alumnos
 * genera mil filas al mes, y abrir esto sin fechas es abrir la primera página de un
 * histórico de cuatro años.
 *
 * ## Las faltas primero no, y es a propósito
 *
 * Es tentador filtrar por «faltó» de entrada, y sería un error: la lista completa es
 * lo que permite ver que **el jueves faltaron ocho**, que es un problema del horario
 * y no de ocho familias.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { cuenta, fecha, fechaParaInput } from '@/shared/ui/formato';
import {
  anterior,
  cursorActual,
  esLaPrimera,
  numeroDePagina,
  PRIMERA,
  siguiente,
  type PilaDeCursores,
} from '@/shared/ui/paginas';
import {
  claseDeAsistencia,
  ESTADOS_DE_ASISTENCIA,
  etiquetaDeAsistencia,
  etiquetaDeOrigen,
  type PaginaDeAsistencia,
} from '@/club/calendario/tipos';
import type { Grupo } from '@/club/grupos/tipos';

/** Hace `dias` días, en `YYYY-MM-DD` local. */
const haceDias = (dias: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return fechaParaInput(d);
};

export function Asistencia({
  club,
  alumnoInicial,
  alAbrirAlumno,
}: {
  club: string;
  /** Llega desde la ficha de un alumno: «ver su asistencia». */
  alumnoInicial?: string;
  alAbrirAlumno: (alumnoId: string) => void;
}) {
  /*
   * Noventa días cuando se viene de un alumno, siete cuando se entra a la pestaña.
   * Ver la cabecera.
   */
  const [desde, setDesde] = useState(() =>
    haceDias(alumnoInicial === undefined ? 7 : 90)
  );
  const [hasta, setHasta] = useState(() => fechaParaInput(new Date()));
  const [grupo, setGrupo] = useState('');
  const [estado, setEstado] = useState('');
  const [alumno, setAlumno] = useState(alumnoInicial ?? '');
  const [cursores, setCursores] = useState<PilaDeCursores>(PRIMERA);
  const cursor = cursorActual(cursores);

  /*
   * Cambiar un filtro vuelve a la primera página. Sin esto, el cursor de la página
   * tres de «todos» se aplica a «solo faltas» y la lista empieza por el medio, que
   * es como se pierde la confianza en una pantalla.
   */
  const filtrar = (poner: () => void) => {
    poner();
    setCursores(PRIMERA);
  };

  const grupos = useQuery({
    queryKey: ['grupos', club],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).groups.get({
        query: {},
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Grupo[];
    },
  });

  const filas = useQuery({
    queryKey: ['asistencia', club, desde, hasta, grupo, estado, alumno, cursor],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).attendance.get({
        query: {
          limit: 50,
          ...(desde === '' ? {} : { from: desde }),
          ...(hasta === '' ? {} : { to: hasta }),
          ...(grupo === '' ? {} : { group_id: grupo }),
          ...(alumno === '' ? {} : { student_id: alumno }),
          ...(estado === '' ? {} : { status: estado as 'absent' }),
          ...(cursor === null ? {} : { cursor }),
        },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as PaginaDeAsistencia;
    },
  });

  return (
    <section>
      <h1>La asistencia</h1>

      <p className="tenue">
        Quién vino y quién no, sin abrir clase por clase. Para decidir horarios
        está el informe de asistencia; esto es para llamar a una familia.
      </p>

      <div className="filtros">
        <label>
          Desde
          <input
            type="date"
            value={desde}
            onChange={(e) => filtrar(() => setDesde(e.target.value))}
          />
        </label>
        <label>
          Hasta
          <input
            type="date"
            value={hasta}
            onChange={(e) => filtrar(() => setHasta(e.target.value))}
          />
        </label>
        <label>
          Grupo
          <select
            value={grupo}
            onChange={(e) => filtrar(() => setGrupo(e.target.value))}
          >
            <option value="">Todos los grupos</option>
            {(grupos.data ?? []).map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Cómo acabó
          <select
            value={estado}
            onChange={(e) => filtrar(() => setEstado(e.target.value))}
          >
            <option value="">Todo</option>
            {ESTADOS_DE_ASISTENCIA.map((e) => (
              <option key={e.id} value={e.id}>
                {e.etiqueta}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/*
        El filtro por alumno no tiene selector: llega desde su ficha. Un desplegable
        de doscientos nombres para una lista que ya se filtra por grupo y fecha sería
        un mando que estorba más de lo que ayuda.
      */}
      {alumno !== '' && (
        <p className="tenue">
          Solo un alumno.{' '}
          <button
            type="button"
            className="btn-plano"
            onClick={() => filtrar(() => setAlumno(''))}
          >
            Ver todos
          </button>
        </p>
      )}

      {filas.isPending && (
        <Cargando filas={8} etiqueta="Cargando la asistencia" />
      )}

      {filas.isError && (
        <PanelError
          problema={aProblema(filas.error)}
          reintentar={() => void filas.refetch()}
        />
      )}

      {filas.data &&
        (filas.data.data.length === 0 ? (
          <Vacio
            titulo="No hay nada en esas fechas"
            explicacion={
              alumno === ''
                ? 'Puede que no se haya pasado lista todavía, o que el periodo se quede corto. La lista se pasa desde la aplicación del maestro.'
                : 'De este alumno no hay asistencia en los últimos tres meses. Ensancha las fechas o quita el filtro para ver el resto del club.'
            }
          />
        ) : (
          <>
            <div className="tabla-marco">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Cuándo</th>
                    <th>Quién</th>
                    <th>Grupo</th>
                    <th>Cómo acabó</th>
                    <th>De dónde sale</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.data.data.map((f) => (
                    <tr key={`${f.session_id}-${f.student_id}`}>
                      <td>
                        {fecha(f.session_date)}
                        {f.venue_name && (
                          <>
                            <br />
                            <span className="tenue">{f.venue_name}</span>
                          </>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="enlace-fila"
                          onClick={() => alAbrirAlumno(f.student_id)}
                        >
                          {f.student_name}
                        </button>
                      </td>
                      <td className="tenue">{f.group_name}</td>
                      <td>
                        <span
                          className={`pastilla ${claseDeAsistencia(f.status)}`}
                        >
                          {etiquetaDeAsistencia(f.status)}
                        </span>
                        {/* Los minutos solo cuando llegó tarde: en el resto no significan nada. */}
                        {f.status === 'late' && f.minutes_late !== null && (
                          <span className="tenue">
                            {' '}
                            {cuenta(f.minutes_late, 'minuto', 'minutos')}
                          </span>
                        )}
                      </td>
                      <td className="tenue">
                        {/*
                          Solo se dice cuando lo puso el sistema. Los otros tres
                          orígenes no cambian la conversación con una familia; «lo
                          puso el sistema» sí, porque significa que nadie lo miró.
                        */}
                        {f.source === 'auto' ? etiquetaDeOrigen(f.source) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="paginacion">
              <button
                type="button"
                className="btn-plano"
                disabled={esLaPrimera(cursores) || filas.isFetching}
                onClick={() => setCursores(anterior)}
              >
                Anterior
              </button>
              <span className="tenue">
                {esLaPrimera(cursores)
                  ? 'Primera página'
                  : `Página ${numeroDePagina(cursores)}`}
              </span>
              <button
                type="button"
                className="btn-plano"
                disabled={!filas.data.has_more || filas.isFetching}
                onClick={() =>
                  setCursores((c) =>
                    siguiente(c, filas.data?.next_cursor ?? null)
                  )
                }
              >
                Siguiente
              </button>
            </div>
          </>
        ))}
    </section>
  );
}
